import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { api, formatPrice, formatApiErrorDetail } from "../lib/api";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { statusLabel } from "../lib/labels";
import { derivePublicName } from "../lib/publicName";
import BecomePartnerPanel from "../components/BecomePartnerPanel";
import BecomeVendorPanel from "../components/BecomeVendorPanel";
import { BadgeCheck, ShieldCheck, FileDown, Sparkles, MessageSquare, Clock, Upload, X, Camera, User } from "lucide-react";
import { toast } from "sonner";
import { generateReceipt } from "../lib/receipt";
import TicketChatModal from "../components/TicketChatModal";

const STATUS_LABELS = {
    solicitado: { es: "Solicitado", en: "Requested" },
    en_revision: { es: "En revisión", en: "Under review" },
    cotizado: { es: "Cotizado", en: "Quoted" },
    aceptado: { es: "Aceptado", en: "Accepted" },
    en_proceso: { es: "En proceso", en: "In process" },
    en_camino_tienda: { es: "En camino a tienda", en: "En route to store" },
    listo_tienda: { es: "Listo en tienda", en: "Ready at store" },
    en_reparto: { es: "En reparto", en: "Out for delivery" },
    entregado: { es: "Entregado", en: "Delivered" },
    cancelado: { es: "Cancelado", en: "Cancelled" },
    rechazado: { es: "Rechazado", en: "Rejected" },
};

const STATUS_FLOW = ["solicitado", "en_revision", "cotizado", "aceptado", "en_proceso", "en_camino_tienda", "listo_tienda", "en_reparto", "entregado"];

export default function Profile() {
    const { user, setUser, loading } = useAuth();
    const { t, lang } = useLanguage();
    const statusFor = (s) => (STATUS_LABELS[s] ? STATUS_LABELS[s][lang] || STATUS_LABELS[s].es : s);
    const nav = useNavigate();
    const [params] = useSearchParams();
    const tab = params.get("tab") || "perfil";
    const [orders, setOrders] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [requests, setRequests] = useState([]);
    const [form, setForm] = useState({ name: "", public_name: "", bio: "", photo: "", social_links: {} });
    const [verifSelfie, setVerifSelfie] = useState("");
    const [verifFile, setVerifFile] = useState("");  // base64 dataURL
    const [openTicket, setOpenTicket] = useState(null); // ticket object currently open in chat modal
    const [appSettings, setAppSettings] = useState({});
    // Partner (afiliado) — cargamos lazy solo si el usuario es socio.
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [partnerUses, setPartnerUses] = useState([]);
    const [partnerLoaded, setPartnerLoaded] = useState(false);

    useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
    useEffect(() => {
        if (user) {
            setForm({ name: user.name || "", public_name: user.public_name || "", bio: user.bio || "", photo: user.photo || "", social_links: user.social_links || {} });
            api.get("/orders/mine").then(({ data }) => setOrders(data));
            api.get("/tickets/mine").then(({ data }) => setTickets(data));
            api.get("/product-requests/mine").then(({ data }) => setRequests(data)).catch(() => {});
            api.get("/settings").then(({ data }) => setAppSettings(data)).catch(() => {});
            // Detect partner status
            api.get("/partner/me").then(({ data }) => {
                if (data?.is_partner) {
                    setPartnerInfo(data);
                    api.get("/partner/me/uses").then(({ data: uses }) => setPartnerUses(uses)).catch(() => {});
                }
            }).catch(() => {}).finally(() => setPartnerLoaded(true));
        }
    }, [user]);

    if (loading || !user) return <div className="p-12 text-center text-[var(--tb-text-2)]">Cargando...</div>;

    const save = async () => {
        try {
            const { data } = await api.put("/auth/profile", form);
            setUser(data);
            toast.success(t("Perfil actualizado", "Profile updated"));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const requestVerify = async () => {
        if (!verifSelfie || !verifFile) { toast.error(t("Sube AMBAS fotos: selfie y documento.", "Upload BOTH photos: selfie and ID document.")); return; }
        try {
            await api.post("/auth/verify-request", { selfie_file: verifSelfie, document_file: verifFile });
            toast.success(t("Solicitud enviada para revisión", "Request submitted for review"));
            setVerifSelfie(""); setVerifFile("");
            try { const { data } = await api.get("/auth/me"); setUser(data); } catch (err) { console.warn("[verify] failed to refresh /auth/me:", err?.message || err); }
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const onPickPhoto = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error(t("Solo imágenes", "Images only")); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error(t("La imagen supera 5MB", "The image exceeds 5MB")); return; }
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const max = 600;  // square crop max for avatar
                const scale = Math.min(1, max / Math.max(img.width, img.height));
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                setForm((f) => ({ ...f, photo: canvas.toDataURL("image/jpeg", 0.85) }));
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    const onPickVerifPhoto = (file, target) => {
        // target: "selfie" | "document"
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error(t("Solo imágenes (JPG/PNG)", "Images only (JPG/PNG)")); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error(t("El archivo supera 5MB", "The file exceeds 5MB")); return; }
        const img = new Image();
        const reader = new FileReader();
        reader.onload = (ev) => {
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const maxW = 1600;
                const scale = Math.min(1, maxW / img.width);
                canvas.width = img.width * scale;
                canvas.height = img.height * scale;
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
                if (target === "selfie") setVerifSelfie(dataUrl);
                else setVerifFile(dataUrl);
            };
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
    };

    const cancelRequest = async (rid) => {
        if (!window.confirm("¿Cancelar esta solicitud?")) return;
        try { await api.post(`/product-requests/${rid}/cancel`); toast.success("Solicitud cancelada"); const { data } = await api.get("/product-requests/mine"); setRequests(data); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const tabs = [
        { id: "perfil", label: t("Perfil", "Profile") },
        { id: "ordenes", label: t("Mis Pedidos", "My Orders") },
        { id: "solicitudes", label: t("Mis Solicitudes", "My Requests") },
        { id: "tickets", label: t("Soporte", "Support") },
        { id: "verificacion", label: t("Verificación", "Verification") },
        ...(partnerInfo
            ? [{ id: "socio", label: t("Mi Código de Socio", "My Partner Code") }]
            : [{ id: "ser-socio", label: t("Ser Socio", "Become a Partner") }]),
        // "Ser Vendedor" tab: hidden for existing vendors/admins.
        ...(user && user.role !== "vendedor" && user.role !== "admin"
            ? [{ id: "ser-vendedor", label: t("Ser Vendedor", "Become a Seller") }]
            : []),
    ];

    return (
        <div className="max-w-5xl mx-auto px-6 py-12 fade-in">
            <div className="flex items-center gap-4 mb-8">
                {user.photo ? <img src={user.photo} alt="" className="w-20 h-20 rounded-full object-cover border border-[var(--tb-gold)]" /> :
                    <div className="w-20 h-20 rounded-full bg-[var(--tb-surface-2)] flex items-center justify-center text-2xl font-display border border-[var(--tb-gold)]">{(user.name || "?")[0]}</div>}
                <div>
                    <div className="font-display text-3xl flex items-center gap-2">
                        {user.name} {user.verified && <BadgeCheck className="w-6 h-6 text-[#2196F3] fill-[#2196F3]/20" />}
                    </div>
                    <div className="text-sm text-[var(--tb-text-2)]">{user.email} · {user.role}</div>
                    {Number(user.wallet_balance || 0) > 0 && (
                        <div className="mt-2 inline-flex items-center gap-2 text-xs bg-[var(--tb-border-gold)]/15 border border-[var(--tb-border-gold)]/30 px-2.5 py-1 rounded-full" data-testid="profile-wallet-balance">
                            <span className="text-[var(--tb-gold)]">💰 {t("Saldo disponible:", "Available balance:")}</span>
                            <span className="text-[var(--tb-gold)] font-semibold">${Number(user.wallet_balance).toFixed(2)}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="border-b border-[var(--tb-border)] flex gap-6 mb-8 overflow-x-auto">
                {tabs.map((t) => (
                    <button key={t.id} onClick={() => nav(`/perfil?tab=${t.id}`)}
                        className={`pb-3 text-sm tracking-wide whitespace-nowrap ${tab === t.id ? "border-b-2 border-[var(--tb-gold)] text-[var(--tb-gold)]" : "text-[var(--tb-text-2)]"}`}
                        data-testid={`profile-tab-${t.id}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "perfil" && (
                <div className="space-y-6 max-w-xl">
                    <div className="tb-card p-6 space-y-4">
                        <div>
                            <label className="tb-overline">{t("Nombre completo", "Full name")}</label>
                            <input className="tb-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
                            <p className="text-[10px] text-[var(--tb-muted)] mt-1">{t("Se usa solo para tu recibo/factura. No se muestra en el sitio.", "Used only for your receipt/invoice. Not shown publicly.")}</p>
                        </div>
                        <div>
                            <label className="tb-overline">{t("Nombre público", "Public name")}</label>
                            <input
                                className="tb-input"
                                value={form.public_name}
                                onChange={(e) => setForm({ ...form, public_name: e.target.value })}
                                placeholder={derivePublicName(form.name) || t("Ej. Julio Cuellar", "E.g. Julio Cuellar")}
                                data-testid="profile-public-name"
                                maxLength={40}
                            />
                            <p className="text-[10px] text-[var(--tb-muted)] mt-1">
                                {t(
                                    `Es lo que verán los demás en la barra superior, en tu perfil de vendedor y en las tarjetas de socios. Si lo dejas vacío, usaremos "${derivePublicName(form.name) || "primer nombre + primer apellido"}" automáticamente.`,
                                    `This is what other people see in the top bar, on your seller profile and on partner cards. If you leave it empty we'll auto-use "${derivePublicName(form.name) || "first name + first last name"}".`
                                )}
                            </p>
                        </div>
                        <div>
                            <label className="tb-overline">{t("Foto de perfil", "Profile photo")}</label>
                            <div className="flex items-center gap-3 mt-1">
                                {form.photo ? (
                                    <img src={form.photo} alt="" className="w-16 h-16 rounded-full object-cover border border-[var(--tb-gold)]" />
                                ) : (
                                    <div className="w-16 h-16 rounded-full bg-[var(--tb-bg-2)] border border-[var(--tb-border)]" />
                                )}
                                <label className="tb-btn-secondary inline-flex items-center gap-2 cursor-pointer !text-xs">
                                    <Upload className="w-4 h-4" /> {form.photo ? t("Cambiar foto", "Change photo") : t("Subir foto", "Upload photo")}
                                    <input type="file" accept="image/*" className="hidden"
                                        onChange={(e) => onPickPhoto(e.target.files?.[0])}
                                        data-testid="profile-photo-file" />
                                </label>
                                {form.photo && (
                                    <button onClick={() => setForm({ ...form, photo: "" })} className="tb-link text-xs" data-testid="profile-photo-clear">{t("Quitar", "Remove")}</button>
                                )}
                            </div>
                        </div>
                        <button onClick={save} className="tb-btn-primary" data-testid="profile-save">Guardar cambios</button>
                    </div>
                    <ChangePasswordCard />
                </div>
            )}

            {tab === "ordenes" && (
                <div className="space-y-4">
                    {orders.length === 0 ? <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">Aún no tienes pedidos.</div> : orders.map((o) => (
                        <div key={o._id} className="tb-card p-5" data-testid={`order-${o._id}`}>
                            <div className="flex justify-between items-start mb-3 flex-wrap gap-3">
                                <div>
                                    <div className="font-mono text-xs text-[var(--tb-muted)]">{o._id}</div>
                                    <div className="text-sm">{new Date(o.created_at).toLocaleString(lang === "en" ? "en-US" : "es-SV")}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[var(--tb-gold)] font-semibold">{formatPrice(o.total)}</div>
                                    <span className="tb-badge tb-badge-gold mt-1 inline-block">{statusLabel(o.status, lang)}</span>
                                </div>
                            </div>
                            <div className="text-sm text-[var(--tb-text-2)]">
                                {o.items.map((i) => `${i.nombre} ×${i.quantity}`).join(", ")}
                            </div>
                            <div className="flex justify-between items-center mt-3 flex-wrap gap-2">
                                <div className="text-xs text-[var(--tb-muted)]">{t("Método", "Method")}: {o.payment_method}</div>
                                <button onClick={async () => {
                                    try {
                                        const settings = await api.get("/settings").then(r => r.data);
                                        await generateReceipt(o, settings);
                                        toast.success(t("Recibo descargado", "Receipt downloaded"));
                                    } catch (e) { toast.error(t("Error al generar recibo", "Error generating receipt")); }
                                }} className="tb-btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5" data-testid={`download-receipt-${o._id}`}>
                                    <FileDown className="w-3.5 h-3.5" /> {t("Descargar Recibo PDF", "Download Receipt PDF")}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "solicitudes" && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center flex-wrap gap-3">
                        <p className="text-sm text-[var(--tb-text-2)]">Vestidos, trajes y prendas a medida que has solicitado.</p>
                        <Link to="/solicitar-producto" className="tb-btn-primary !text-sm inline-flex items-center gap-2" data-testid="profile-new-request">
                            <Sparkles className="w-4 h-4" /> Nueva solicitud
                        </Link>
                    </div>
                    {requests.length === 0 ? (
                        <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">
                            Aún no has solicitado prendas a medida. <Link to="/solicitar-producto" className="text-[var(--tb-gold)] underline">Solicitar ahora</Link>
                        </div>
                    ) : requests.map((r) => {
                        const currentIdx = STATUS_FLOW.indexOf(r.status);
                        const isCancel = r.status === "cancelado" || r.status === "rechazado";
                        return (
                        <div key={r._id} className="tb-card p-5" data-testid={`request-${r._id}`}>
                            <div className="flex justify-between items-start mb-3 flex-wrap gap-3">
                                <div>
                                    <div className="font-mono text-xs text-[var(--tb-muted)]">{r._id}</div>
                                    <div className="font-medium">{r.items.length} prenda(s) · Para {r.needed_date}</div>
                                    <div className="text-xs text-[var(--tb-text-2)]">Creada: {new Date(r.created_at).toLocaleDateString("es-SV")}</div>
                                </div>
                                <div className="text-right">
                                    <span className={`tb-badge ${isCancel ? "tb-badge-muted" : "tb-badge-gold"}`}>{statusFor(r.status)}</span>
                                    {r.quoted_price && <div className="text-[var(--tb-gold)] font-semibold mt-1">{formatPrice(r.quoted_price)}</div>}
                                </div>
                            </div>

                            {/* Timeline */}
                            {!isCancel && (
                                <div className="my-4 flex items-center gap-1 overflow-x-auto pb-2">
                                    {STATUS_FLOW.map((st, idx) => (
                                        <React.Fragment key={`${r._id}-st-${st}`}>
                                            <div className={`flex flex-col items-center min-w-[60px] ${idx <= currentIdx ? "text-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`}>
                                                <div className={`w-2.5 h-2.5 rounded-full ${idx <= currentIdx ? "bg-[var(--tb-gold)]" : "bg-[var(--tb-border)]"}`} />
                                                <div className="text-[9px] mt-1 text-center leading-tight">{statusFor(st)}</div>
                                            </div>
                                            {idx < STATUS_FLOW.length - 1 && <div className={`h-px flex-1 ${idx < currentIdx ? "bg-[var(--tb-gold)]" : "bg-[var(--tb-border)]"}`} />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}

                            <div className="text-sm text-[var(--tb-text-2)] mb-3">
                                {r.items.map((it, idx) => (
                                    <div key={`${r._id}-it-${idx}`}>· {it.name} {it.size && `(${it.size})`}</div>
                                ))}
                            </div>

                            {r.tracking_number && (
                                <div className="text-xs bg-[var(--tb-bg-2)]/40 p-2 rounded mb-2">
                                    <span className="text-[var(--tb-muted)]">Nº de guía:</span> <span className="font-mono">{r.tracking_number}</span>
                                </div>
                            )}

                            <div className="flex flex-wrap gap-2 mt-3">
                                {r.ticket_id && (
                                    <Link to={`/soporte?tid=${r.ticket_id}`} className="tb-btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5">
                                        <MessageSquare className="w-3.5 h-3.5" /> Abrir chat
                                    </Link>
                                )}
                                {!isCancel && r.status !== "entregado" && (
                                    <button onClick={() => cancelRequest(r._id)} className="text-red-500 text-xs hover:underline ml-auto" data-testid={`cancel-request-${r._id}`}>
                                        Cancelar solicitud
                                    </button>
                                )}
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {tab === "tickets" && (
                <div>
                    <Link to="/soporte" className="tb-btn-secondary inline-block mb-4">+ Nuevo ticket</Link>
                    <div className="space-y-3">
                        {tickets.length === 0 ? <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">Sin tickets abiertos.</div> : tickets.map((t) => {
                            const lastMsg = t.messages?.[t.messages.length - 1];
                            const preview = (lastMsg?.text || "").slice(0, 120);
                            const isTransfer = t.tag === "transfer_payment";
                            return (
                                <div key={t._id} className="tb-card p-4 hover:border-[var(--tb-gold)]/60 transition-colors cursor-pointer"
                                    onClick={() => setOpenTicket(t)}
                                    data-testid={`ticket-card-${t._id}`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-mono text-[10px] text-[var(--tb-muted)]">{t._id}</span>
                                                <span className={`tb-badge ${t.status === "closed" ? "tb-badge-muted" : "tb-badge-gold"}`}>{t.status}</span>
                                                {isTransfer && <span className="tb-badge tb-badge-success">💸 Transferencia</span>}
                                                <span className="text-[10px] text-[var(--tb-muted)]">{t.messages?.length || 0} mensajes</span>
                                            </div>
                                            <div className="font-medium text-sm mt-1 truncate">{t.subject}</div>
                                            {preview && (
                                                <div className="text-xs text-[var(--tb-text-2)] mt-1 truncate">
                                                    <span className="text-[var(--tb-gold)]">{lastMsg.from === "admin" ? "Soporte" : "Tú"}:</span> {preview}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setOpenTicket(t); }}
                                            className="tb-btn-primary !text-xs !px-3 !py-1.5 inline-flex items-center gap-1 flex-shrink-0"
                                            data-testid={`ticket-open-chat-${t._id}`}>
                                            <MessageSquare className="w-3.5 h-3.5" />
                                            Abrir chat
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <TicketChatModal
                        open={!!openTicket}
                        ticket={openTicket}
                        onClose={() => setOpenTicket(null)}
                        onUpdated={(updated) => {
                            setTickets((prev) => prev.map((x) => x._id === updated._id ? updated : x));
                            setOpenTicket(updated);
                        }}
                        settings={appSettings}
                    />
                </div>
            )}

            {tab === "verificacion" && (
                <div className="tb-card p-6 max-w-2xl">
                    <ShieldCheck className="w-8 h-8 text-[var(--tb-gold)] mb-3" />
                    <h3 className="font-display text-2xl mb-2">{t("Verificación de identidad", "Identity verification")}</h3>
                    {user.verified ? (
                        <p className="text-[#4CAF50] flex items-center gap-2"><BadgeCheck className="w-5 h-5" /> Tu cuenta está verificada.</p>
                    ) : user.verification_status === "pending" ? (
                        <p className="text-[#FF9800]">Tu solicitud está en revisión.</p>
                    ) : (
                        <>
                            <p className="text-[var(--tb-text-2)] mb-4 text-sm">
                                Para verificarte necesitas enviar <strong className="text-[var(--tb-gold)]">dos fotos obligatorias</strong>:
                                una selfie sosteniendo tu documento o claramente visible, y una foto frontal de tu documento de identidad (DUI/Pasaporte/Licencia).
                                Puedes <strong>subir archivos</strong> desde tu dispositivo o <strong>tomar la foto al instante</strong> con tu cámara.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-4 mb-4">
                                {/* SELFIE */}
                                <div>
                                    <label className="tb-overline flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Selfie *</label>
                                    <div className="mt-2 aspect-square w-full border-2 border-dashed border-[var(--tb-border)] rounded-lg overflow-hidden bg-[var(--tb-bg-2)]/30 flex items-center justify-center relative">
                                        {verifSelfie ? (
                                            <>
                                                <img src={verifSelfie} alt="Selfie" className="w-full h-full object-cover" />
                                                <button onClick={() => setVerifSelfie("")} className="absolute top-2 right-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg" data-testid="verify-selfie-remove">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <User className="w-12 h-12 text-[var(--tb-muted)]" />
                                        )}
                                    </div>
                                    {!verifSelfie && (
                                        <div className="flex gap-2 mt-2">
                                            <label className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5 cursor-pointer flex-1 justify-center">
                                                <Camera className="w-3.5 h-3.5" /> Tomar foto
                                                <input type="file" accept="image/*" capture="user" className="hidden"
                                                    onChange={(e) => onPickVerifPhoto(e.target.files?.[0], "selfie")}
                                                    data-testid="verify-selfie-camera" />
                                            </label>
                                            <label className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5 cursor-pointer flex-1 justify-center">
                                                <Upload className="w-3.5 h-3.5" /> Subir
                                                <input type="file" accept="image/*" className="hidden"
                                                    onChange={(e) => onPickVerifPhoto(e.target.files?.[0], "selfie")}
                                                    data-testid="verify-selfie-upload" />
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {/* DOCUMENTO */}
                                <div>
                                    <label className="tb-overline flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> Documento de identidad *</label>
                                    <div className="mt-2 aspect-square w-full border-2 border-dashed border-[var(--tb-border)] rounded-lg overflow-hidden bg-[var(--tb-bg-2)]/30 flex items-center justify-center relative">
                                        {verifFile ? (
                                            <>
                                                <img src={verifFile} alt="Documento" className="w-full h-full object-cover" />
                                                <button onClick={() => setVerifFile("")} className="absolute top-2 right-2 bg-rose-500 hover:bg-rose-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg" data-testid="verify-doc-remove">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <FileDown className="w-12 h-12 text-[var(--tb-muted)]" />
                                        )}
                                    </div>
                                    {!verifFile && (
                                        <div className="flex gap-2 mt-2">
                                            <label className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5 cursor-pointer flex-1 justify-center">
                                                <Camera className="w-3.5 h-3.5" /> Tomar foto
                                                <input type="file" accept="image/*" capture="environment" className="hidden"
                                                    onChange={(e) => onPickVerifPhoto(e.target.files?.[0], "document")}
                                                    data-testid="verify-doc-camera" />
                                            </label>
                                            <label className="tb-btn-secondary !text-xs inline-flex items-center gap-1.5 cursor-pointer flex-1 justify-center">
                                                <Upload className="w-3.5 h-3.5" /> Subir
                                                <input type="file" accept="image/*" className="hidden"
                                                    onChange={(e) => onPickVerifPhoto(e.target.files?.[0], "document")}
                                                    data-testid="verify-doc-upload" />
                                            </label>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="text-[10px] text-[var(--tb-muted)] mb-4 p-3 border border-dashed border-[var(--tb-border)] rounded">
                                🔒 Tus fotos se guardan cifradas y solo las verá el equipo de administración para validar tu identidad.
                                Una vez aprobada tu verificación, recibirás el <span className="text-[#2196F3] font-semibold">check azul</span> en tu perfil.
                            </div>

                            <button
                                onClick={requestVerify}
                                disabled={!verifSelfie || !verifFile}
                                className="tb-btn-primary disabled:opacity-50 inline-flex items-center gap-2"
                                data-testid="verify-submit">
                                <ShieldCheck className="w-4 h-4" /> Solicitar verificación
                            </button>
                        </>
                    )}
                </div>
            )}

            {tab === "socio" && partnerInfo && (
                <PartnerMePanel partner={partnerInfo} uses={partnerUses} />
            )}
            {tab === "socio" && !partnerInfo && partnerLoaded && (
                <div className="tb-card p-8 text-center text-sm text-[var(--tb-text-2)]">
                    {t("Aún no eres socio. Contacta al equipo si quieres tu código de afiliado.", "You are not a partner yet. Contact the team if you want your affiliate code.")}
                </div>
            )}
            {tab === "ser-socio" && !partnerInfo && (
                <BecomePartnerPanel user={user} />
            )}
            {tab === "ser-vendedor" && user.role !== "vendedor" && user.role !== "admin" && (
                <BecomeVendorPanel user={user} />
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Partner panel — visible in the profile only when the current user has a
// partner code linked to their account. Shows stats + a full table of uses.
// -----------------------------------------------------------------------------
function PartnerMePanel({ partner, uses }) {
    const { t, lang } = useLanguage();
    const stats = partner.stats || {};
    const modeLabel = partner.commission_mode === "commission"
        ? `${t("Comisión", "Commission")} ${Number(partner.commission_rate || 0)}%`
        : t("Solo tracking", "Tracking only");
    const [editingBio, setEditingBio] = useState(false);
    const [bio, setBio] = useState(partner.bio || "");
    const [savingBio, setSavingBio] = useState(false);

    const saveBio = async () => {
        setSavingBio(true);
        try {
            await api.patch("/partner/me", { bio });
            toast.success(t("Bio actualizada", "Bio updated"));
            setEditingBio(false);
            // Reload the profile so the top card reflects the change.
            window.location.reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
        finally { setSavingBio(false); }
    };

    const copyCode = () => {
        try { navigator.clipboard.writeText(partner.code); toast.success(t(`Copiado: ${partner.code}`, `Copied: ${partner.code}`)); }
        catch { toast.error(t("No se pudo copiar", "Could not copy")); }
    };

    return (
        <div className="space-y-4">
            <div className="tb-card p-5 flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <div className="tb-overline text-[var(--tb-gold)]">{t("Mi código", "My code")}</div>
                    <button
                        type="button" onClick={copyCode}
                        className="mt-1 inline-flex items-center gap-2 text-2xl font-display font-mono text-[var(--tb-gold)] hover:opacity-80"
                        title={t("Copiar", "Copy")}
                        data-testid="partner-me-copy-code"
                    >
                        {partner.code}
                    </button>
                    <p className="text-xs text-[var(--tb-text-2)] mt-2 leading-relaxed">
                        {t("Comparte este código con tus amigos y seguidores. Cuando lo usen al comprar, la venta se te acredita.", "Share this code with friends and followers. Whenever they use it at checkout, the sale is credited to you.")}
                    </p>
                    <div className="text-[10px] text-[var(--tb-muted)] mt-2 uppercase tracking-wider">{modeLabel}</div>
                    {partner.commission_mode === "commission" && Number(stats.commission_due_now || 0) > 0 && (
                        <div className="mt-2 text-xs bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 rounded px-2 py-1 inline-block">
                            💸 {t("Pendiente de pago:", "Pending payout:")} <strong>${Number(stats.commission_due_now).toFixed(2)}</strong>
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-3 min-w-[260px]">
                    <div className="tb-card p-3 text-center">
                        <div className="text-2xl font-semibold text-[var(--tb-gold)]">{stats.uses_confirmed || 0}</div>
                        <div className="text-[10px] text-[var(--tb-muted)] uppercase">{t("Ventas confirmadas", "Confirmed sales")}</div>
                    </div>
                    <div className="tb-card p-3 text-center">
                        <div className="text-2xl font-semibold">${(stats.revenue_confirmed || 0).toFixed(2)}</div>
                        <div className="text-[10px] text-[var(--tb-muted)] uppercase">{t("Ingresos generados", "Revenue generated")}</div>
                    </div>
                    {partner.commission_mode === "commission" && (
                        <>
                            <div className="tb-card p-3 text-center">
                                <div className="text-2xl font-semibold text-emerald-400">${(stats.commission_confirmed || 0).toFixed(2)}</div>
                                <div className="text-[10px] text-[var(--tb-muted)] uppercase">{t("Comisión ganada", "Commission earned")}</div>
                            </div>
                            <div className="tb-card p-3 text-center">
                                <div className="text-2xl font-semibold text-amber-300">{stats.uses_pending || 0}</div>
                                <div className="text-[10px] text-[var(--tb-muted)] uppercase">{t("Pendientes", "Pending")}</div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Public card editor — the partner controls what visitors see on /vendedores. */}
            <div className="tb-card p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="font-display text-lg">{t("Tarjeta pública en /vendedores", "Public card on /vendedores")}</h3>
                    {!editingBio && (
                        <button onClick={() => setEditingBio(true)} className="tb-btn-secondary !text-xs" data-testid="partner-me-edit-bio">
                            {t("Editar", "Edit")}
                        </button>
                    )}
                </div>
                {editingBio ? (
                    <div className="space-y-2">
                        <textarea className="tb-input min-h-[90px]" value={bio} onChange={(e) => setBio(e.target.value)}
                            maxLength={400}
                            placeholder={t("Cuéntale a los compradores un poco sobre ti — máx. 400 caracteres.", "Tell buyers a bit about yourself — max 400 characters.")}
                            data-testid="partner-me-bio-input" />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => { setBio(partner.bio || ""); setEditingBio(false); }} className="tb-btn-secondary !text-xs">{t("Cancelar", "Cancel")}</button>
                            <button onClick={saveBio} disabled={savingBio} className="tb-btn-primary !text-xs disabled:opacity-50" data-testid="partner-me-bio-save">
                                {savingBio ? t("Guardando…", "Saving…") : t("Guardar", "Save")}
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-[var(--tb-text-2)] italic">
                        {partner.bio || t("Aún no has escrito una bio. Añade una para presentarte a los compradores.", "You haven't written a bio yet. Add one to introduce yourself to buyers.")}
                    </p>
                )}
            </div>

            <div className="tb-card p-4">
                <h3 className="font-display text-lg mb-1">{t("Historial de usos", "Usage history")} ({uses.length})</h3>
                <p className="text-[10px] text-[var(--tb-muted)] mb-3 italic">
                    {t(
                        "Por protección de los compradores, no compartimos su nombre ni sus datos personales. Solo verás la referencia opaca del pedido y su monto.",
                        "To protect buyers, we don't share their names or personal data. You'll only see an opaque order reference and its amount."
                    )}
                </p>
                {uses.length === 0 ? (
                    <p className="text-sm text-[var(--tb-text-2)] text-center py-6">{t("Aún nadie ha usado tu código. ¡Compártelo!", "No one has used your code yet. Share it!")}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[10px] uppercase text-[var(--tb-muted)] border-b border-[var(--tb-border)]">
                                    <th className="p-2 text-left">{t("Fecha", "Date")}</th>
                                    <th className="p-2 text-left">{t("Referencia", "Reference")}</th>
                                    <th className="p-2 text-right">{t("Subtotal", "Subtotal")}</th>
                                    {partner.commission_mode === "commission" && <th className="p-2 text-right">{t("Comisión", "Commission")}</th>}
                                    <th className="p-2 text-center">{t("Estado", "Status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {uses.map((u) => (
                                    <tr key={u._id} className="border-b border-[var(--tb-border)] hover:bg-[var(--tb-bg-2)]/30">
                                        <td className="p-2 whitespace-nowrap">{new Date(u.created_at).toLocaleDateString(lang === "en" ? "en-US" : "es-SV")}</td>
                                        <td className="p-2 font-mono">{u.order_ref || "—"}</td>
                                        <td className="p-2 text-right">${(u.subtotal || 0).toFixed(2)}</td>
                                        {partner.commission_mode === "commission" && (
                                            <td className="p-2 text-right text-emerald-400">${(u.commission_amount || 0).toFixed(2)}</td>
                                        )}
                                        <td className="p-2 text-center">
                                            <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${
                                                u.effective_status === "confirmed" ? "bg-emerald-500/20 text-emerald-300" :
                                                u.effective_status === "cancelled" ? "bg-rose-500/20 text-rose-300" :
                                                "bg-amber-500/20 text-amber-300"
                                            }`}>{u.effective_status}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}


function ChangePasswordCard() {
    const { t } = useLanguage();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 6) { toast.error(t("Mínimo 6 caracteres", "Minimum 6 characters")); return; }
        if (next !== confirm) { toast.error(t("Las contraseñas no coinciden", "Passwords do not match")); return; }
        setLoading(true);
        try {
            await api.post("/auth/password-change", { current_password: current, new_password: next });
            toast.success(t("Contraseña actualizada", "Password updated"));
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setLoading(false); }
    };

    return (
        <div className="tb-card p-6 space-y-3">
            <div className="tb-overline text-[var(--tb-gold)]">{t("Cambiar contraseña", "Change password")}</div>
            <form onSubmit={submit} className="space-y-3">
                <input type="password" className="tb-input" placeholder={t("Contraseña actual", "Current password")} value={current} onChange={(e) => setCurrent(e.target.value)} required data-testid="cp-current" />
                <input type="password" className="tb-input" placeholder={t("Nueva contraseña", "New password")} value={next} onChange={(e) => setNext(e.target.value)} minLength={6} required data-testid="cp-new" />
                <input type="password" className="tb-input" placeholder={t("Confirmar nueva contraseña", "Confirm new password")} value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={6} required data-testid="cp-confirm" />
                <button type="submit" disabled={loading} className="tb-btn-secondary !text-sm disabled:opacity-50" data-testid="cp-submit">
                    {loading ? t("Guardando...", "Saving...") : t("Actualizar contraseña", "Update password")}
                </button>
            </form>
        </div>
    );
}


